import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A hook that persists input value in localStorage.
 * localStorage writes are debounced to avoid blocking the main thread on
 * every keystroke — the in-memory state updates immediately while the
 * persistence is batched.
 * @param key The localStorage key to use for persistence
 * @param initialValue Optional initial value if nothing is stored
 * @returns [value, setValue] tuple similar to useState
 */
export function usePersistedInput(key: string, initialValue = ""): [string, (value: string) => void] {
    const [value, setRawValue] = useState<string>(() => {
        if (typeof window !== "undefined") {
            const storedValue = localStorage.getItem(key);
            return storedValue !== null ? storedValue : initialValue;
        }
        return initialValue;
    });

    const pendingWriteRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestValueRef = useRef(value);

    // Flush any pending write on unmount so input isn't lost
    useEffect(() => {
        return () => {
            if (pendingWriteRef.current !== null) {
                clearTimeout(pendingWriteRef.current);
                if (typeof window !== "undefined") {
                    localStorage.setItem(key, latestValueRef.current);
                }
            }
        };
    }, [key]);

    const setValue = useCallback(
        (newValue: string) => {
            setRawValue(newValue);
            latestValueRef.current = newValue;

            // Debounce localStorage writes to avoid blocking the main thread
            if (pendingWriteRef.current !== null) {
                clearTimeout(pendingWriteRef.current);
            }
            pendingWriteRef.current = setTimeout(() => {
                pendingWriteRef.current = null;
                if (typeof window !== "undefined") {
                    localStorage.setItem(key, newValue);
                }
            }, 300);
        },
        [key],
    );

    return [value, setValue];
}
